import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Info, OctagonAlert } from 'lucide-react';
import { useShiftSheetDraft } from './ShiftSheetDraftContext';
import { savePendingRows } from './shiftSheetRules';
import { segmentErrorMessage } from './segmentRules';

/** UI-12 — sticky footer: pending-row count, Discard, Save (sequential per-row saves, stop at the first error). */
export function ShiftSheetFooter({ onSaved }) {
  const { pending, setInlineError } = useShiftSheetDraft();
  const [saving, setSaving] = useState(false);
  const count = pending.length;
  const saveAll = async () => {
    setSaving(true);
    try {
      const { failed } = await savePendingRows(pending);
      if (failed) setInlineError(failed.id, segmentErrorMessage(failed.error));
      else onSaved && onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="sticky bottom-0 z-10 bg-background border-t -mx-6 px-6 py-2 flex items-center justify-between gap-2" data-testid="shift-sheet-footer">
      <span className="text-xs text-muted-foreground" data-testid="sheet-pending-count">
        {count === 0 ? 'No unsaved changes' : `${count} unsaved change${count === 1 ? '' : 's'}`}
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={count === 0 || saving} onClick={() => pending.forEach((r) => r.discard && r.discard())} data-testid="sheet-discard-btn">Discard</Button>
        <Button size="sm" className="h-7 text-xs" disabled={count === 0 || saving} onClick={saveAll} data-testid="sheet-save-btn">{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </div>
  );
}

const ICON = { danger: OctagonAlert, warning: AlertTriangle, info: Info };
const TONE = { danger: 'border-red-300 bg-red-50 text-red-900', warning: 'border-amber-300 bg-amber-50 text-amber-900', info: 'border-sky-300 bg-sky-50 text-sky-900' };

/** UI-03 — the single actionable alert line. */
export function ShiftAlertLine({ alert, onAction }) {
  if (!alert) return null;
  const Icon = ICON[alert.tone] || Info;
  return (
    <div className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-[11px] ${TONE[alert.tone] || TONE.info}`} data-testid="shift-alert-line" data-kind={alert.kind}>
      <span className="flex items-center gap-1.5 min-w-0"><Icon className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{alert.text}</span></span>
      {alert.action && <Button size="sm" variant="outline" className="h-6 text-[11px] shrink-0 bg-white/70" onClick={() => onAction && onAction(alert.action)} data-testid="shift-alert-action">{alert.action.label}</Button>}
    </div>
  );
}
